import React from 'react';
import Box from 'apollo-react/components/Box';
import Button from 'apollo-react/components/Button';
import Typography from 'apollo-react/components/Typography';
import ProfileAttachmentBlock, {
  AttachmentSlotRevisionHistory,
} from './ProfileAttachmentBlock';
import { ProfileWizardSingleFileField } from './profileWizardFileFields';
import {
  openProfileFilePreviewTab,
  downloadProfileFileBlob,
} from '../profileFileAxiosAccess';
import profileStyles from '../../customerProfile/profileUi/Profile.module.css';

/**
 * ID document: same presentation as license “Copy of current / previous …” (wizard FileUpload + panel).
 */
export default function IdDocumentUploadSection({
  label = 'Copy of current / previous ID document',
  /** @deprecated FileFieldInput shows pending selection */
  localSelectionLabel,
  existingFile,
  /** New pending file (edit mode); parent owns state */
  localFile = null,
  attachmentRevisions = [],
  slotKey = 'general:id_image',
  compact = false,
  /** When false, hide prior-version / audit list (edit mode included). */
  showPriorVersions = true,
  validationConfig,
  onFileChange,
  helperText,
  editable = true,
  fileError,
  /** Use monolith-style divider + label/value grid + attachment panel (customer profile cards). */
  profileCardStyle = false,
  /** Stable id for Apollo FileUpload */
  fileFieldId = 'hcp_profile_id_document',
}) {
  const serverFileId =
    existingFile && existingFile.id != null && existingFile.id !== ''
      ? existingFile.id
      : null;
  const serverFileDownloadName =
    existingFile?.originalName ||
    existingFile?.original_name ||
    existingFile?.name ||
    '';

  const editBody = (
    <>
      <ProfileWizardSingleFileField
        id={fileFieldId}
        label={label}
        preset="idDocument"
        validationConfig={validationConfig}
        localFile={localFile}
        existingServerFile={existingFile}
        onFileChange={onFileChange}
        helperText={helperText}
        showError={!!fileError}
      />
      {fileError ? (
        <Typography variant="bodySmall" color="error" sx={{ mt: 0.5 }}>
          {fileError}
        </Typography>
      ) : null}
      {editable && serverFileId != null ? (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            mt: 1,
            alignItems: 'center',
          }}
        >
          <Button
            variant="secondary"
            size="small"
            type="button"
            onClick={() => {
              void openProfileFilePreviewTab(serverFileId);
            }}
          >
            Preview
          </Button>
          <Button
            variant="secondary"
            size="small"
            type="button"
            onClick={() => {
              void downloadProfileFileBlob(
                serverFileId,
                serverFileDownloadName
              );
            }}
          >
            Download
          </Button>
        </Box>
      ) : null}
      {showPriorVersions ? (
        <AttachmentSlotRevisionHistory
          slotKey={slotKey}
          attachmentRevisions={attachmentRevisions}
          title="Prior ID uploads (audit)"
        />
      ) : null}
    </>
  );

  const viewBody = (
    <>
      {localSelectionLabel ? (
        <span className="text-body">{localSelectionLabel}</span>
      ) : (
        <ProfileAttachmentBlock
          file={existingFile}
          slotKey={slotKey}
          attachmentRevisions={attachmentRevisions}
          compact={compact}
          showPriorVersions={showPriorVersions}
        />
      )}
    </>
  );

  const body = editable ? editBody : viewBody;

  if (profileCardStyle) {
    return (
      <>
        <div className={profileStyles.divider} />
        {editable ? (
          <div className={profileStyles.attachmentPanel}>{body}</div>
        ) : (
          <div className={profileStyles.row}>
            <span className={profileStyles.label}>{label}:</span>
            <div className={profileStyles.valueColumn}>
              <div className={profileStyles.attachmentPanel}>{body}</div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      {!editable ? (
        <Typography variant="bodySmall" color="text.secondary" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      ) : null}
      <div>{body}</div>
    </Box>
  );
}
